/**
 * ItemRow Component
 * Displays a single shopping list item
 */

import React from 'react';
import { Trash2, Tag } from 'lucide-react';
import { isItemOnPromo } from '../services/nettoDealsService';
import type { Item } from '../api/types';

interface ItemRowProps {
  item: Item;
  onToggle: (id: number) => void;
  onDelete: (id: number) => void;
}

/**
 * Row component for displaying a shopping list item
 */
export function ItemRow({ item, onToggle, onDelete }: ItemRowProps) {
  const isPromo = isItemOnPromo(item.name);
  
  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors group">
      {/* Checkbox */}
      <button
        onClick={() => onToggle(item.id)}
        className="flex-shrink-0 w-6 h-6 rounded border-2 border-slate-300 hover:border-[#FFD100] flex items-center justify-center transition-colors"
        aria-label={item.checked ? 'Marquer comme non fait' : 'Marquer comme fait'}
      >
        {item.checked && (
          <svg className="w-4 h-4 text-[#FFD100]" fill="currentColor" viewBox="0 0 20 20">
            <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
          </svg>
        )}
      </button>

      {/* Item info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`font-semibold text-slate-900 ${
            item.checked ? 'line-through opacity-50' : ''
          }`}>
            {item.name}
          </span>
          {isPromo && (
            <span className="inline-flex items-center gap-1 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
              <Tag size={10} />
              PROMO
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">
            {item.category || 'Non classé'}
          </span>
          {item.quantity > 1 && (
            <span className="text-xs font-bold text-slate-700">
              × {item.quantity}
            </span>
          )}
        </div>
      </div>

      {/* Delete button */}
      <button
        onClick={() => onDelete(item.id)}
        className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Supprimer l'article"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}
